import { createTheme, Theme } from '@mui/material/styles';

export const getAppTheme = (mode: 'light' | 'dark'): Theme => {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: isLight ? '#fafafa' : '#1a2332',
        paper: isLight ? '#ffffff' : '#223044',
      },
      text: {
        primary: isLight ? '#111827' : '#e8eaed',
        secondary: isLight ? '#374151' : '#b0b8c4',
      },
      divider: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)',
      custom: {
        surfaceElevated: isLight ? '#f5f5f5' : '#2a3a4e',
        borderLight: isLight ? '#e0e0e0' : '#3a4a5e',
        subtleText: isLight ? '#64748b' : '#9baab8',
        disabledText: isLight ? '#cbd5e1' : '#5a6a7e',
        tableRowBg: isLight ? '#ffffff' : '#223044',
        tableRowHoverBg: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isLight ? '#fafafa' : '#1a2332',
            // Ambient gradient lives on the body so the whole viewport — behind the
            // glass sidebar, the content gap, and the dashboard — shares one
            // continuous background (no seam between sidebar and content).
            backgroundImage: isLight
              ? `radial-gradient(60% 50% at 15% 20%, rgba(33,150,243,0.06), transparent 70%),
                 radial-gradient(55% 45% at 85% 30%, rgba(0,137,123,0.05), transparent 70%),
                 radial-gradient(70% 55% at 50% 95%, rgba(76,175,80,0.05), transparent 70%)`
              : `radial-gradient(60% 50% at 15% 20%, rgba(33,150,243,0.14), transparent 70%),
                 radial-gradient(55% 45% at 85% 30%, rgba(0,137,123,0.12), transparent 70%),
                 radial-gradient(70% 55% at 50% 95%, rgba(76,175,80,0.10), transparent 70%)`,
            backgroundAttachment: 'fixed',
            color: isLight ? '#111827' : '#e0e0e0',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      // Glass / translucent module sidebar — matches the dashboard's glassmorphism
      // so the sidebar no longer reads as a stark white panel against the dashboard.
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(34,48,68,0.72)',
            backdropFilter: 'blur(16px) saturate(160%)',
            WebkitBackdropFilter: 'blur(16px) saturate(160%)',
            backgroundImage: isLight
              ? `radial-gradient(120% 55% at 0% 0%, rgba(33,150,243,0.08), transparent 60%),
                 radial-gradient(120% 50% at 0% 100%, rgba(76,175,80,0.06), transparent 60%)`
              : `radial-gradient(120% 55% at 0% 0%, rgba(33,150,243,0.14), transparent 60%),
                 radial-gradient(120% 50% at 0% 100%, rgba(76,175,80,0.10), transparent 60%)`,
            borderRight: `1px solid ${isLight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.08)'}`,
            // Solid fallback where backdrop-filter is unsupported, so the sidebar stays legible.
            '@supports not ((backdrop-filter: blur(16px)) or (-webkit-backdrop-filter: blur(16px)))': {
              backgroundColor: isLight ? 'rgba(252,252,254,0.98)' : 'rgba(34,48,68,0.97)',
            },
          },
        },
      },
    },
  });
};
